import { useEffect, useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import Reveal from './Reveal.jsx';
import { CONTACT_INFO } from '../data/site.js';

/* ── Fill these 3 keys to send real email. Until then the form runs in demo mode. ── */
const EMAILJS_PUBLIC_KEY = 'YOUR_PUBLIC_KEY';
const EMAILJS_SERVICE_ID = 'YOUR_SERVICE_ID';
const EMAILJS_TEMPLATE_ID = 'YOUR_TEMPLATE_ID';
const RECEIVERS = ['vinothrasamani@gmail.com', 'er.sanjayh@gmail.com'];
const LIVE = EMAILJS_PUBLIC_KEY !== 'YOUR_PUBLIC_KEY';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null); // { type, title, msg }
  const timer = useRef(null);

  useEffect(() => {
    if (LIVE) emailjs.init(EMAILJS_PUBLIC_KEY);
    return () => clearTimeout(timer.current);
  }, []);

  const showToast = (type, title, msg) => {
    setToast({ type, title, msg });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), 5500);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    if (form.company.value) return; // honeypot

    const email = form.from_email.value.trim();
    if (!EMAIL_RE.test(email)) {
      showToast('error', 'Check your email', 'Please enter a valid email address.');
      form.from_email.focus();
      return;
    }
    if (form.message.value.trim().length < 10) {
      showToast('error', 'Add a little more', 'Your message should be at least 10 characters.');
      form.message.focus();
      return;
    }

    setBusy(true);
    const params = {
      from_firstname: form.from_firstname.value.trim(),
      from_lastname: form.from_lastname.value.trim(),
      from_email: email,
      from_phone: form.from_phone.value.trim() || 'Not provided',
      message: form.message.value.trim(),
      sent_time: new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }),
      to_name: 'Kanaga Dental Clinic',
      reply_to: email,
    };

    try {
      if (!LIVE) {
        await new Promise((r) => setTimeout(r, 1200));
        showToast('success', 'Demo Mode', 'Message captured. Add EmailJS keys in Contact.jsx to send for real.');
        form.reset();
        return;
      }
      for (const receiver of RECEIVERS) {
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, { ...params, to_email: receiver });
      }
      showToast('success', 'Message Sent!', "Thank you — we'll get back to you shortly.");
      form.reset();
    } catch (err) {
      console.error(err);
      showToast('error', 'Couldn’t send', 'Something went wrong. Please try again or call us directly.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <Reveal className="section-header">
          <div className="section-label">Get In Touch</div>
          <h2 className="section-title">Contact Us</h2>
          <p className="section-desc">
            Have a question or need more information? Send us a message and we'll get back to you
            shortly.
          </p>
        </Reveal>

        <div className="contact-grid">
          <Reveal className="contact-info">
            <div className="ci-card">
              <h3>Clinic Information</h3>
              <p>We're here to help. Visit us at either of our two branches.</p>
              {CONTACT_INFO.map((c) => (
                <div className="ci-row" key={c.label}>
                  <div className="ci-icon">{c.icon}</div>
                  <div className="ci-text">
                    <h4>{c.label}</h4>
                    <p>{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="contact-form-wrap" delay={80}>
            <div className="form-card">
              <h3>Send Us a Message</h3>
              <p>We'll get back to you as soon as possible.</p>
              <form onSubmit={onSubmit} noValidate>
                <input
                  type="text"
                  name="company"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                />
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="fname">First Name *</label>
                    <input id="fname" name="from_firstname" placeholder="Your first name" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lname">Last Name *</label>
                    <input id="lname" name="from_lastname" placeholder="Your last name" required />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input id="email" type="email" name="from_email" placeholder="your@email.com" required />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input id="phone" type="tel" name="from_phone" placeholder="Your phone number" />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea id="message" name="message" placeholder="How can we help you?" required />
                </div>
                <button type="submit" className="submit-btn" disabled={busy}>
                  <span className="spinner" style={{ display: busy ? 'block' : 'none' }} />
                  <span>{busy ? 'Sending…' : '✉️ Send Message'}</span>
                </button>
              </form>
              <p className="form-notice">🔒 Your information is kept private and never shared.</p>
            </div>
          </Reveal>
        </div>
      </div>

      <div className={`toast ${toast ? `${toast.type} show` : ''}`} role="status" aria-live="polite">
        <div className="toast-icon">{toast?.type === 'success' ? '✅' : toast ? '❌' : ''}</div>
        <div className="toast-body">
          <h4>{toast?.title}</h4>
          <p>{toast?.msg}</p>
        </div>
      </div>
    </section>
  );
}
